// Minimal markdown editor — textarea with a tiny toolbar that inserts the
// usual tokens. No rendering library; we render the preview as plain text
// with line-breaks. Good-enough for MVP per UX-spec 03.

import { useRef } from "preact/hooks";
import { Bold, Italic, List, ListOrdered } from "lucide-preact";

interface Props {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  rows?: number;
  placeholder?: string;
}

export function MarkdownEditor(props: Props): preact.JSX.Element {
  const { value, onChange, id, rows = 6, placeholder } = props;
  const ref = useRef<HTMLTextAreaElement>(null);

  const wrap = (left: string, right = left): void => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    const next = `${before}${left}${sel || "Text"}${right}${after}`;
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const newStart = before.length + left.length;
      const newEnd = newStart + (sel || "Text").length;
      ta.setSelectionRange(newStart, newEnd);
    });
  };

  const prefixLines = (prefix: string): void => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = value.slice(0, start);
    const sel = value.slice(start, end) || "Listenpunkt";
    const after = value.slice(end);
    const transformed = sel
      .split("\n")
      .map((line, i) =>
        prefix === "1. " ? `${i + 1}. ${line}` : `${prefix}${line}`
      )
      .join("\n");
    const next = `${before}${transformed}${after}`;
    onChange(next);
    requestAnimationFrame(() => ta.focus());
  };

  return (
    <div class="flex flex-col gap-1.5">
      <div
        class="flex items-center gap-0.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] p-1"
        role="toolbar"
        aria-label="Markdown-Werkzeuge"
      >
        <ToolBtn label="Fett" onClick={() => wrap("**")}>
          <Bold size={14} aria-hidden="true" />
        </ToolBtn>
        <ToolBtn label="Kursiv" onClick={() => wrap("_")}>
          <Italic size={14} aria-hidden="true" />
        </ToolBtn>
        <ToolBtn label="Liste" onClick={() => prefixLines("- ")}>
          <List size={14} aria-hidden="true" />
        </ToolBtn>
        <ToolBtn label="Nummerierte Liste" onClick={() => prefixLines("1. ")}>
          <ListOrdered size={14} aria-hidden="true" />
        </ToolBtn>
      </div>
      <textarea
        ref={ref}
        id={id}
        value={value}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        rows={rows}
        placeholder={placeholder}
        class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-surface-container-lowest)] transition-shadow resize-y"
      />
    </div>
  );
}

function ToolBtn(props: {
  label: string;
  onClick: () => void;
  children: preact.ComponentChildren;
}): preact.JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[28px] min-w-[28px] inline-flex items-center justify-center"
      aria-label={props.label}
      title={props.label}
    >
      {props.children}
    </button>
  );
}
